import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import {
  PREFERENCE_DISPLAY,
  LEVEL_LABELS,
  PREFERENCE_LEVELS,
} from '../../config/searchPreferences';

const PanelContainer = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin: 16px 0;
  border: 1px solid #e2e8f0;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategorySection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PreferenceField = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldLabel = styled.label`
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  flex: 1;
`;

const LevelSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  background: white;
  color: #334155;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  &:hover {
    border-color: #94a3b8;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button`
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const ResetButton = styled(Button)`
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  
  &:hover {
    background: #fde68a;
  }
`;

const HelpText = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 16px 0 0 0;
  line-height: 1.5;
`;

function PreferencesPanel({ preferences, onUpdatePreference, onResetToDefault }) {
  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>
          <FontAwesomeIcon icon={faCog} />
          Modifier vos préférences
        </PanelTitle>
      </PanelHeader>

      <HelpText>
        ✓ Vos préférences sont conservées dans votre navigateur et ne sont jamais envoyées à Open Food Facts ou à qui que ce soit d'autre.
      </HelpText>

      {Object.entries(PREFERENCE_DISPLAY).map(([categoryKey, categoryData]) => (
        <CategorySection key={categoryKey}>
          <CategoryTitle>{categoryData.title}</CategoryTitle>
          
          {Object.entries(categoryData.fields).map(([fieldKey, fieldLabel]) => (
            <PreferenceField key={`${categoryKey}-${fieldKey}`}>
              <FieldLabel htmlFor={`pref-${categoryKey}-${fieldKey}`}>
                {fieldLabel}
              </FieldLabel>
              <LevelSelect
                id={`pref-${categoryKey}-${fieldKey}`}
                value={preferences[categoryKey]?.[fieldKey] || 'not_important'}
                onChange={(e) => onUpdatePreference(categoryKey, fieldKey, e.target.value)}
              >
                <option value={PREFERENCE_LEVELS.NOT_IMPORTANT}>
                  {LEVEL_LABELS.not_important}
                </option>
                <option value={PREFERENCE_LEVELS.IMPORTANT}>
                  {LEVEL_LABELS.important}
                </option>
                <option value={PREFERENCE_LEVELS.VERY_IMPORTANT}>
                  {LEVEL_LABELS.very_important}
                </option>
                <option value={PREFERENCE_LEVELS.MANDATORY}>
                  {LEVEL_LABELS.mandatory}
                </option>
              </LevelSelect>
            </PreferenceField>
          ))}
        </CategorySection>
      ))}

      <ActionButtons>
        <ResetButton
          type="button"
          onClick={onResetToDefault}
          title="Réinitialiser toutes les préférences aux valeurs par défaut"
        >
          <FontAwesomeIcon icon={faRotateLeft} />
          Réinitialiser
        </ResetButton>
      </ActionButtons>
    </PanelContainer>
  );
}

export default PreferencesPanel;
